import React, { Suspense } from 'react';
import { motion } from 'framer-motion';
import { AlertCircle } from 'lucide-react';
import ErrorBoundary from './ErrorBoundary';

/**
 * Loading component with shimmer effect
 */
export const ComponentLoader = ({ height = 'auto', message = 'Loading...' }) => (
  <div className="flex flex-col items-center justify-center p-8" style={{ minHeight: height }}>
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent mb-3"></div>
    <p className="text-sm text-muted-foreground">{message}</p>
  </div>
);

/**
 * Skeleton loader for dashboard cards
 */
export const DashboardCardSkeleton = () => (
  <div className="bg-card border border-border rounded-lg p-6 animate-pulse">
    <div className="h-4 bg-muted rounded w-1/3 mb-4"></div>
    <div className="h-8 bg-muted rounded w-1/2 mb-3"></div>
    <div className="h-3 bg-muted rounded w-2/3"></div>
  </div>
);

/**
 * Skeleton loader for analytics charts
 */
export const AnalyticsChartSkeleton = () => (
  <div className="bg-card border border-border rounded-lg p-6 animate-pulse">
    <div className="h-5 bg-muted rounded w-1/4 mb-6"></div>
    <div className="space-y-3">
      <div className="h-32 bg-muted rounded"></div>
      <div className="flex gap-4">
        <div className="h-4 bg-muted rounded flex-1"></div>
        <div className="h-4 bg-muted rounded flex-1"></div>
        <div className="h-4 bg-muted rounded flex-1"></div>
      </div>
    </div>
  </div>
);

/**
 * Skeleton loader for table rows
 */
export const TableRowSkeleton = ({ columns = 4, rows = 5 }) => (
  <div className="space-y-3 animate-pulse">
    {Array.from({ length: rows })?.map((_, i) => (
      <div key={i} className="flex gap-4">
        {Array.from({ length: columns })?.map((_, j) => (
          <div key={j} className="h-12 bg-muted rounded flex-1"></div>
        ))}
      </div>
    ))}
  </div>
);

/**
 * Error fallback component
 */
const LazyLoadError = ({ error, resetError }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="bg-destructive/10 border border-destructive/20 rounded-lg p-6 text-center"
  >
    <AlertCircle className="w-12 h-12 text-destructive mx-auto mb-4" />
    <h3 className="text-lg font-semibold text-foreground mb-2">Failed to load component</h3>
    <p className="text-sm text-muted-foreground mb-4">
      {error?.message || 'An error occurred while loading this component'}
    </p>
    <button
      onClick={resetError}
      className="px-4 py-2 bg-accent text-accent-foreground rounded-lg hover:bg-accent/90 transition-colors"
    >
      Try Again
    </button>
  </motion.div>
);

/**
 * Lazy load boundary with loading states and error handling
 */
export const LazyLoadBoundary = ({ 
  children, 
  fallback = <ComponentLoader />,
  errorFallback,
  onError
}) => {
  return (
    <ErrorBoundary
      fallback={errorFallback || LazyLoadError}
      onError={onError}
    >
      <Suspense fallback={fallback}>
        {children}
      </Suspense>
    </ErrorBoundary>
  );
};

export default LazyLoadBoundary;